import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'components/Modal';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { useAnalytics } from 'hooks/useAnalytics';
import { formatterGbp, classNames, toDecimal } from 'utils';
import { Skeleton } from 'components/Skeleton';
import { handleImageError } from 'helpers';
import { useTheme } from 'context/ThemeContext';
import { InvestOffer, TabTypes } from '../types';
import {
  CwRatings,
  DrinkingWindow,
  FaqItem,
  MarketMetrics as MarketMetricsData,
  PriceAndPerformance as PriceAndPerformanceData,
  ProducerProfile,
  SiblingOffer,
} from '../__mocks__/offerDetail/types';
import { useOfferDetail } from '../hooks/useOfferDetail';
import InvestComponent from '../components/InvestComponent';

interface OfferDetailModalProps {
  offer: InvestOffer | null;
  isOpen: boolean;
  sourceRow?: string;
  onClose: () => void;
  onInvestNow: (offer: InvestOffer) => void;
}

const SECTIONS = [
  { id: 'at-a-glance', label: 'At a glance' },
  { id: 'market-metrics', label: 'Market metrics' },
  { id: 'the-opportunity', label: 'The opportunity' },
  { id: 'price-performance', label: 'Price & performance' },
  { id: 'tasting-notes', label: 'Tasting notes' },
  { id: 'the-producer', label: 'The producer' },
  { id: 'faqs', label: 'FAQs' },
];

const OfferDetailModal: FC<OfferDetailModalProps> = ({ offer, isOpen, sourceRow, onClose, onInvestNow }) => {
  const { data: detail, isLoading } = useOfferDetail({
    lwin18: offer?.lwin18,
    assetId: offer?.assetId,
    wineName: offer?.name,
  });
  const { track } = useAnalytics();

  const ratings = detail?.ratings;
  const tastingNotes = detail?.tastingNotes ?? undefined;
  const drinkingWindow = detail?.drinkingWindow ?? undefined;
  const marketMetrics = detail?.marketMetrics ?? undefined;

  // modal_open: fires once per (offer, open=true) transition.
  useEffect(() => {
    if (!isOpen || !offer) return;
    track('modal_open', { wine_id: offer.lwin18, source_row: sourceRow });
  }, [isOpen, offer, sourceRow, track]);

  const marketPrice = useMemo(() => {
    const last = detail?.priceAndPerformance?.history?.slice(-1)[0];
    return offer?.marketValue ?? last?.marketPrice ?? null;
  }, [detail, offer]);

  const discountPct = useMemo(() => {
    if (!offer || marketPrice == null || marketPrice === 0) return null;
    return Math.round(((marketPrice - offer.priceGbp) / marketPrice) * 1000) / 10;
  }, [offer, marketPrice]);

  if (!offer) return null;

  const ariaLabel = `${offer.name} ${offer.vintage} offer detail`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={ariaLabel}
      onSectionChange={(sectionId) =>
        track('modal_section_view', { wine_id: offer.lwin18, source_row: sourceRow, section_id: sectionId })
      }
    >
      <Modal.Main>
        <Hero offer={offer} ratings={ratings} drinkingWindow={drinkingWindow} loading={isLoading} />
        <Modal.AnchorNav />
        <Modal.Section id={SECTIONS[0].id} label={SECTIONS[0].label} title="At a glance">
          <AtAGlance
            offer={offer}
            ratings={ratings}
            drinkingWindow={drinkingWindow}
            marketMetrics={marketMetrics}
            loading={isLoading}
          />
        </Modal.Section>
        <Modal.Section id={SECTIONS[1].id} label={SECTIONS[1].label} title="Market metrics">
          <MarketMetricsSection ratings={ratings} marketMetrics={marketMetrics} loading={isLoading} />
        </Modal.Section>
        <Modal.Section
          id={SECTIONS[2].id}
          label={SECTIONS[2].label}
          title={
            <span>
              The opportunity{' '}
              {detail?.aiSnippet?.attribution && (
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  · Written by {detail.aiSnippet.attribution}
                </span>
              )}
            </span>
          }
        >
          <TheOpportunity prose={detail?.aiSnippet?.prose} loading={isLoading} />
        </Modal.Section>
        <Modal.Section id={SECTIONS[3].id} label={SECTIONS[3].label} title="Price & performance">
          <PriceAndPerformance data={detail?.priceAndPerformance} loading={isLoading} />
        </Modal.Section>
        <Modal.Section id={SECTIONS[4].id} label={SECTIONS[4].label} title="Tasting notes & drinking window">
          <TastingNotes notes={tastingNotes} drinkingWindow={drinkingWindow} loading={isLoading} />
        </Modal.Section>
        <Modal.Section id={SECTIONS[5].id} label={SECTIONS[5].label} title="The producer">
          <TheProducer producer={detail?.producer ?? undefined} siblings={detail?.siblings ?? []} loading={isLoading} />
        </Modal.Section>
        <Modal.Section id={SECTIONS[6].id} label={SECTIONS[6].label} title="FAQs">
          <Faqs faqs={detail?.faqs ?? []} loading={isLoading} />
        </Modal.Section>
      </Modal.Main>
      <Modal.Rail>
        <Rail
          offer={offer}
          marketPrice={marketPrice}
          discountPct={discountPct}
          onInvestNow={() => {
            track('cta_click_primary', { wine_id: offer.lwin18, source_row: sourceRow });
            onInvestNow(offer);
          }}
        />
      </Modal.Rail>
      <Modal.MobileActionBar>
        <MobileActionBar
          offer={offer}
          onInvestNow={() => {
            track('cta_click_primary', { wine_id: offer.lwin18, source_row: sourceRow });
            onInvestNow(offer);
          }}
        />
      </Modal.MobileActionBar>
    </Modal>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Hero
// ────────────────────────────────────────────────────────────────────────────

interface HeroProps {
  offer: InvestOffer;
  ratings: CwRatings | undefined;
  drinkingWindow: DrinkingWindow | undefined;
  loading: boolean;
}

const Hero: FC<HeroProps> = ({ offer, ratings, drinkingWindow, loading }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[170px_1fr] gap-5 pb-5 border-b border-stone dark:border-slate-700 mb-2">
      <div className="h-[220px] rounded-lg overflow-hidden bg-stone dark:bg-slate-700 flex items-center justify-center p-5">
        {offer.mainImage ? (
          <img
            src={offer.mainImage}
            alt={`${offer.name} ${offer.vintage}`}
            className="max-h-full max-w-full object-contain"
            onError={handleImageError}
          />
        ) : (
          <span className="text-sm text-gray-500">Bottle image</span>
        )}
      </div>
      <div>
        <div className="flex flex-wrap gap-2 mb-2">
          {ratings ? (
            <span className="text-sm font-semibold px-2 py-1 rounded bg-green text-white">
              {toDecimal(ratings.critic)} · Outstanding
            </span>
          ) : loading ? (
            <Skeleton width={110} height={22} />
          ) : null}
          {offer.region && (
            <span className="text-sm border border-gray-300 dark:border-slate-600 px-2 py-1 rounded text-gray-700 dark:text-gray-200">
              {offer.region}
            </span>
          )}
        </div>
        <h2 className="text-20 md:text-md font-medium text-vine dark:text-white">
          {offer.name}
          {offer.vintage ? <span className="ml-2 font-normal">{offer.vintage}</span> : null}
        </h2>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {[offer.unitSize, offer.numberOfUnitsAllowed ? `${offer.numberOfUnitsAllowed} unit included` : null]
            .filter(Boolean)
            .join(' · ')}
        </div>
        <div className="text-14 text-vine dark:text-teal-300 font-semibold mt-3">
          {drinkingWindow ? (
            `Drinking window opens ${drinkingWindow.drinkFrom}`
          ) : loading ? (
            <Skeleton width={180} height={16} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Sections
// ────────────────────────────────────────────────────────────────────────────

interface AtAGlanceProps {
  offer: InvestOffer;
  ratings: CwRatings | undefined;
  drinkingWindow: DrinkingWindow | undefined;
  marketMetrics: MarketMetricsData | undefined;
  loading: boolean;
}

const AtAGlance: FC<AtAGlanceProps> = ({ offer, ratings: r, drinkingWindow: dw, marketMetrics: mm, loading }) => {
  if (loading && !r && !dw && !mm) return <SkeletonList count={4} />;
  const bullets: ReactNode[] = [];

  // Undervaluation from market metrics
  if (mm?.valuePct != null) {
    const valuePct = Math.abs(mm.valuePct);
    const isUndervalued = mm.valuePct < 0;
    bullets.push(
      <span key="value">
        {isUndervalued ? (
          <span className="text-green font-semibold">Undervalued by {valuePct.toFixed(1)}%</span>
        ) : (
          <span>Overvalued by {valuePct.toFixed(1)}%</span>
        )}
        {mm.livExMarketPrice != null && ` · Market price ${formatterGbp.format(mm.livExMarketPrice)}`}
      </span>,
    );
  }

  if (r)
    bullets.push(
      `Critic score ${toDecimal(r.critic)} · Vintage score ${r.vintage} · Liquidity ${toDecimal(r.liquidity)}`,
    );
  if (r)
    bullets.push(
      `Fair Value ${Math.round(r.fairValue)} · Performance ${Math.round(r.performance)} · Grade ${r.grade}/10`,
    );

  // Performance from market metrics
  if (mm?.perf5y != null || mm?.perf1y != null) {
    const perfParts: string[] = [];
    if (mm.perf1y != null) perfParts.push(`1Y: ${(mm.perf1y * 100).toFixed(1)}%`);
    if (mm.perf3y != null) perfParts.push(`3Y: ${(mm.perf3y * 100).toFixed(1)}%`);
    if (mm.perf5y != null) perfParts.push(`5Y: ${(mm.perf5y * 100).toFixed(1)}%`);
    if (perfParts.length > 0) bullets.push(`Performance: ${perfParts.join(' · ')}`);
  }

  if (dw) bullets.push(`Drinking window ${dw.drinkFrom}–${dw.drinkBy}, peak around ${dw.peak}`);
  if (offer.quantityOutstanding != null)
    bullets.push(`${offer.quantityOutstanding} of ${offer.initialQuantity} units remaining`);
  return (
    <ul className="space-y-2">
      {bullets.map((b, i) => (
        <li key={i} className="pl-5 relative text-14 text-gray-700 dark:text-gray-200">
          <span className="absolute left-0 top-0 text-green font-bold">✓</span>
          {b}
        </li>
      ))}
    </ul>
  );
};

interface MarketMetricsSectionProps {
  ratings: CwRatings | undefined;
  marketMetrics: MarketMetricsData | undefined;
  loading: boolean;
}

const MarketMetricsSection: FC<MarketMetricsSectionProps> = ({ ratings, marketMetrics: mm, loading }) => {
  if (loading && !ratings && !mm) return <TileGridSkeleton />;
  if (!ratings && !mm) return null;

  const tiles: { label: string; value: ReactNode; highlight?: boolean }[] = [
    { label: 'Vintage', value: toDecimal(ratings?.vintage) },
    { label: 'Critic', value: toDecimal(ratings?.critic) },
    { label: 'Fair Value', value: ratings?.fairValue != null ? Math.round(ratings.fairValue) : '—' },
    { label: 'Liquidity', value: ratings?.liquidity != null ? Math.round(ratings.liquidity) : '—' },
    { label: 'Performance', value: ratings?.performance != null ? Math.round(ratings.performance) : '—' },
    { label: 'Grade', value: ratings?.grade != null ? `${ratings.grade}/10` : '—' },
  ];

  // Additional market metrics tiles
  const marketTiles: { label: string; value: ReactNode; highlight?: boolean }[] = [];

  if (mm?.valuePct != null) {
    const isUndervalued = mm.valuePct < 0;
    marketTiles.push({
      label: 'Valuation',
      value: (
        <span className={isUndervalued ? 'text-green' : 'text-orange'}>
          {isUndervalued ? '−' : '+'}
          {Math.abs(mm.valuePct).toFixed(1)}%
        </span>
      ),
      highlight: isUndervalued,
    });
  }

  if (mm?.livExMarketPrice != null) {
    marketTiles.push({ label: 'Liv-Ex Price', value: formatterGbp.format(mm.livExMarketPrice) });
  }

  if (mm?.winesearcherMarketPrice != null) {
    marketTiles.push({ label: 'WS Price', value: formatterGbp.format(mm.winesearcherMarketPrice) });
  }

  if (mm?.perf1y != null) {
    const perf = mm.perf1y * 100;
    marketTiles.push({
      label: '1Y Perf',
      value: (
        <span className={perf >= 0 ? 'text-green' : 'text-red-500'}>
          {perf >= 0 ? '+' : ''}
          {perf.toFixed(1)}%
        </span>
      ),
    });
  }

  if (mm?.perf5y != null) {
    const perf = mm.perf5y * 100;
    marketTiles.push({
      label: '5Y Perf',
      value: (
        <span className={perf >= 0 ? 'text-green' : 'text-red-500'}>
          {perf >= 0 ? '+' : ''}
          {perf.toFixed(1)}%
        </span>
      ),
    });
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-[auto_1fr] gap-5 items-start">
        <div className="h-[170px] w-full md:w-[200px] flex items-center justify-center col-span-2 md:col-span-1">
          <RadarChart
            vintage={ratings?.vintage ?? 0}
            critic={ratings?.critic ?? 0}
            fairValue={ratings?.fairValue ?? 0}
            liquidity={ratings?.liquidity ?? 0}
            performance={ratings?.performance ?? 0}
            size={170}
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          {tiles.map((t) => (
            <div key={t.label} className="border border-gray-300 dark:border-slate-600 rounded p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t.label}</div>
              <div className="text-20 font-medium text-vine dark:text-white mt-1">{t.value}</div>
            </div>
          ))}
        </div>
      </div>
      {marketTiles.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">Market Data</div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            {marketTiles.map((t) => (
              <div
                key={t.label}
                className={classNames(
                  'border rounded p-3',
                  t.highlight ? 'border-green bg-green/5' : 'border-gray-300 dark:border-slate-600',
                )}
              >
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t.label}</div>
                <div className="text-14 font-medium text-vine dark:text-white mt-1">{t.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TheOpportunity: FC<{ prose: string | undefined; loading: boolean }> = ({ prose, loading }) => {
  if (loading && !prose) return <SkeletonList count={3} />;
  if (!prose) return null;
  return <p className="text-14 text-gray-700 dark:text-gray-200 leading-relaxed">{prose}</p>;
};

const TRADES_PER_PAGE = 5;

const PriceAndPerformance: FC<{ data: PriceAndPerformanceData | undefined; loading: boolean }> = ({
  data,
  loading,
}) => {
  const { isDark } = useTheme();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [tradesPage, setTradesPage] = useState(0);

  // Check if we have history data or can build from trades
  const hasHistory = data?.history && data.history.length > 0;
  const hasTrades = data?.trades && data.trades.length > 0;

  // Pagination calculations
  const totalTrades = data?.trades?.length ?? 0;
  const totalPages = Math.ceil(totalTrades / TRADES_PER_PAGE);
  const paginatedTrades = useMemo(() => {
    if (!data?.trades) return [];
    const start = tradesPage * TRADES_PER_PAGE;
    return data.trades.slice(start, start + TRADES_PER_PAGE);
  }, [data?.trades, tradesPage]);

  const chartOptions = useMemo<Highcharts.Options>(() => {
    // If we have history, use it for the chart
    if (hasHistory && data?.history) {
      const marketPriceData = data.history.map((p) => ({
        x: new Date(p.date).getTime(),
        y: p.marketPrice,
      }));

      const offerPriceData = data.history.map((p) => ({
        x: new Date(p.date).getTime(),
        y: p.offerPrice,
      }));

      // Add critic events as plot lines
      const plotLines = (data.criticEvents || []).map((event) => ({
        color: isDark ? '#94a3b8' : '#6b7280',
        width: 1,
        value: new Date(event.date).getTime(),
        dashStyle: 'Dash' as Highcharts.DashStyleValue,
        label: {
          text: `${event.criticName}: ${event.score}`,
          style: {
            color: isDark ? '#94a3b8' : '#6b7280',
            fontSize: '10px',
          },
        },
      }));

      return buildChartOptions(marketPriceData, offerPriceData, plotLines, isDark);
    }

    // If no history but we have trades, build chart from trades
    if (hasTrades && data?.trades) {
      const tradeData = data.trades
        .map((t) => ({
          x: new Date(t.date).getTime(),
          y: t.price,
        }))
        .sort((a, b) => a.x - b.x);

      return buildTradesChartOptions(tradeData, isDark);
    }

    return {};
  }, [data, hasHistory, hasTrades, isDark]);

  if (loading && !data) return <Skeleton height={200} className="w-full" />;
  if (!data) return null;

  const hasChartData = hasHistory || hasTrades;

  return (
    <div className="space-y-4">
      {hasChartData ? (
        <div className="rounded-lg overflow-hidden">
          <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
        </div>
      ) : (
        <div className="h-[200px] border border-dashed border-gray-300 dark:border-slate-600 rounded-lg flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
          No price history available
        </div>
      )}
      {hasTrades && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Recent trades {totalTrades > TRADES_PER_PAGE && `(${totalTrades})`}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTradesPage((p) => Math.max(0, p - 1))}
                  disabled={tradesPage === 0}
                  className={classNames(
                    'px-2 py-1 text-xs rounded border transition-colors',
                    tradesPage === 0
                      ? 'border-gray-200 dark:border-slate-700 text-gray-300 dark:text-slate-600 cursor-not-allowed'
                      : 'border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700',
                  )}
                >
                  Prev
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {tradesPage + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setTradesPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={tradesPage >= totalPages - 1}
                  className={classNames(
                    'px-2 py-1 text-xs rounded border transition-colors',
                    tradesPage >= totalPages - 1
                      ? 'border-gray-200 dark:border-slate-700 text-gray-300 dark:text-slate-600 cursor-not-allowed'
                      : 'border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700',
                  )}
                >
                  Next
                </button>
              </div>
            )}
          </div>
          <table className="w-full text-14 text-gray-700 dark:text-gray-200">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-stone dark:border-slate-700">
                <th className="py-2">Date</th>
                <th className="py-2">Unit size</th>
                <th className="py-2 text-right">Price</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTrades.map((t, idx) => (
                <tr key={`${t.date}-${idx}`} className="border-b border-stone dark:border-slate-700 last:border-0">
                  <td className="py-2">{t.date}</td>
                  <td className="py-2">{t.unitSize}</td>
                  <td className="py-2 text-right">{formatterGbp.format(t.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Helper to build chart options for history data
function buildChartOptions(
  marketPriceData: { x: number; y: number }[],
  offerPriceData: { x: number; y: number }[],
  plotLines: Highcharts.XAxisPlotLinesOptions[],
  isDark: boolean,
): Highcharts.Options {
  return {
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      height: 200,
      style: { fontFamily: 'inherit' },
    },
    accessibility: { enabled: false },
    title: { text: undefined },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      floating: false,
      itemStyle: { color: isDark ? '#e2e8f0' : '#374151', fontSize: '11px' },
      itemHoverStyle: { color: isDark ? '#fff' : '#000' },
    },
    credits: { enabled: false },
    xAxis: {
      type: 'datetime',
      labels: {
        style: { color: isDark ? '#94a3b8' : '#6b7280', fontSize: '10px' },
        format: "{value:%b '%y}",
      },
      lineColor: isDark ? '#475569' : '#e5e7eb',
      tickColor: isDark ? '#475569' : '#e5e7eb',
      plotLines,
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        style: { color: isDark ? '#94a3b8' : '#6b7280', fontSize: '10px' },
        formatter() {
          return `£${((this.value as number) / 1).toLocaleString()}`;
        },
      },
      gridLineColor: isDark ? '#334155' : '#f3f4f6',
    },
    tooltip: {
      shared: true,
      useHTML: true,
      backgroundColor: isDark ? '#1e293b' : '#ffffff',
      borderColor: isDark ? '#475569' : '#e5e7eb',
      style: { color: isDark ? '#e2e8f0' : '#374151' },
      formatter() {
        const points = this.points || [];
        const date = new Date(this.x as number);
        const dateStr = date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
        let html = `<div style="font-size: 11px; font-weight: 600; margin-bottom: 4px;">${dateStr}</div>`;
        points.forEach((point) => {
          html += `<div style="font-size: 11px;"><span style="color: ${point.color};">●</span> ${
            point.series.name
          }: <strong>${formatterGbp.format(point.y as number)}</strong></div>`;
        });
        return html;
      },
    },
    plotOptions: {
      line: {
        marker: {
          enabled: false,
          states: { hover: { enabled: true, radius: 4 } },
        },
      },
    },
    series: [
      { name: 'Market Price', type: 'line', data: marketPriceData, color: '#10b981', lineWidth: 2 },
      {
        name: 'Offer Price',
        type: 'line',
        data: offerPriceData,
        color: '#f97316',
        lineWidth: 2,
        dashStyle: 'ShortDash',
      },
    ] as Highcharts.SeriesOptionsType[],
  };
}

// Helper to build chart options from trades data only
function buildTradesChartOptions(tradeData: { x: number; y: number }[], isDark: boolean): Highcharts.Options {
  return {
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      height: 200,
      style: { fontFamily: 'inherit' },
    },
    accessibility: { enabled: false },
    title: { text: undefined },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      floating: false,
      itemStyle: { color: isDark ? '#e2e8f0' : '#374151', fontSize: '11px' },
      itemHoverStyle: { color: isDark ? '#fff' : '#000' },
    },
    credits: { enabled: false },
    xAxis: {
      type: 'datetime',
      labels: {
        style: { color: isDark ? '#94a3b8' : '#6b7280', fontSize: '10px' },
        format: "{value:%b '%y}",
      },
      lineColor: isDark ? '#475569' : '#e5e7eb',
      tickColor: isDark ? '#475569' : '#e5e7eb',
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        style: { color: isDark ? '#94a3b8' : '#6b7280', fontSize: '10px' },
        formatter() {
          return `£${((this.value as number) / 1).toLocaleString()}`;
        },
      },
      gridLineColor: isDark ? '#334155' : '#f3f4f6',
    },
    tooltip: {
      shared: true,
      useHTML: true,
      backgroundColor: isDark ? '#1e293b' : '#ffffff',
      borderColor: isDark ? '#475569' : '#e5e7eb',
      style: { color: isDark ? '#e2e8f0' : '#374151' },
      formatter() {
        const points = this.points || [];
        const date = new Date(this.x as number);
        const dateStr = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        let html = `<div style="font-size: 11px; font-weight: 600; margin-bottom: 4px;">${dateStr}</div>`;
        points.forEach((point) => {
          html += `<div style="font-size: 11px;"><span style="color: ${point.color};">●</span> ${
            point.series.name
          }: <strong>${formatterGbp.format(point.y as number)}</strong></div>`;
        });
        return html;
      },
    },
    plotOptions: {
      line: {
        marker: {
          enabled: true,
          radius: 4,
          states: { hover: { enabled: true, radius: 6 } },
        },
      },
    },
    series: [
      { name: 'Trade Price', type: 'line', data: tradeData, color: '#6366f1', lineWidth: 2 },
    ] as Highcharts.SeriesOptionsType[],
  };
}

const TastingNotes: FC<{ notes: string | undefined; drinkingWindow: DrinkingWindow | undefined; loading: boolean }> = ({
  notes,
  drinkingWindow,
  loading,
}) => {
  if (loading && !notes) return <SkeletonList count={3} />;
  return (
    <div className="space-y-4">
      {notes && <blockquote className="text-14 italic text-gray-700 dark:text-gray-200">“{notes}”</blockquote>}
      {drinkingWindow && (
        <div className="relative h-10 rounded-full border border-gray-300 dark:border-slate-600 bg-gradient-to-r from-white via-green-100 to-white dark:from-slate-700 dark:via-emerald-900 dark:to-slate-700">
          <span className="absolute left-[28%] top-1/2 -translate-y-1/2 text-xs bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded px-2 py-1">
            Drink from {drinkingWindow.drinkFrom}
          </span>
          <span className="absolute left-[58%] top-1/2 -translate-y-1/2 text-xs bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded px-2 py-1">
            Peak {drinkingWindow.peak}
          </span>
          <span className="absolute right-[5%] top-1/2 -translate-y-1/2 text-xs bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded px-2 py-1">
            Drink by {drinkingWindow.drinkBy}
          </span>
        </div>
      )}
    </div>
  );
};

const TheProducer: FC<{ producer: ProducerProfile | undefined; siblings: SiblingOffer[]; loading: boolean }> = ({
  producer,
  siblings,
  loading,
}) => {
  if (loading && !producer) return <SkeletonList count={3} />;
  if (!producer) return null;
  return (
    <div className="space-y-4">
      <div className="text-14 text-gray-700 dark:text-gray-200">
        <div className="font-medium text-vine dark:text-white">{producer.name}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {[producer.region, producer.appellation, producer.founded ? `Est. ${producer.founded}` : null]
            .filter(Boolean)
            .join(' · ')}
        </div>
        <p className="mt-2">{producer.philosophy}</p>
      </div>
      {siblings.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            More from this producer
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {siblings.map((s) => (
              <div
                key={s.id}
                className="flex-none w-[160px] border border-stone dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-800"
              >
                <div className="text-14 font-medium text-vine dark:text-white leading-tight">
                  {s.name} {s.vintage}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{s.unitSize}</div>
                <div className="text-sm font-medium text-vine dark:text-white mt-2">
                  {formatterGbp.format(s.priceGbp)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Faqs: FC<{ faqs: FaqItem[]; loading: boolean }> = ({ faqs, loading }) => {
  if (loading && faqs.length === 0) return <SkeletonList count={2} />;
  return (
    <dl className="space-y-4">
      {faqs.map((f, i) => (
        <div key={i}>
          <dt className="font-medium text-vine dark:text-white">{f.question}</dt>
          <dd className="text-14 text-gray-700 dark:text-gray-200 mt-1">{f.answer}</dd>
        </div>
      ))}
    </dl>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Rail + Mobile action bar
// ────────────────────────────────────────────────────────────────────────────

interface RailProps {
  offer: InvestOffer;
  marketPrice: number | null;
  discountPct: number | null;
  onInvestNow: () => void;
}

const Rail: FC<RailProps> = ({ offer, marketPrice, discountPct, onInvestNow }) => {
  const remaining = `${offer.quantityOutstanding} / ${offer.initialQuantity} left`;
  return (
    <div className="flex flex-col">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Offer price</div>
      <div className="text-md font-medium text-vine dark:text-white mt-1">{formatterGbp.format(offer.priceGbp)}</div>
      <dl className="mt-4 space-y-0">
        <RailRow
          label="Market price"
          value={offer?.marketValue != null ? formatterGbp.format(offer?.marketValue) : '—'}
        />
        <RailRow
          label="Discount"
          value={
            discountPct != null ? (
              <span
                className={classNames(
                  'font-semibold',
                  discountPct > 0 ? 'text-green' : 'text-gray-700 dark:text-gray-200',
                )}
              >
                {discountPct > 0 ? '−' : ''}
                {Math.abs(discountPct).toFixed(1)}%
              </span>
            ) : (
              '—'
            )
          }
        />
        <RailRow label="Unit size" value={offer.unitSize} />
        <RailRow label="Availability" value={<span className="text-orange font-semibold">{remaining}</span>} />
      </dl>
      <button
        type="button"
        onClick={onInvestNow}
        className="mt-4 w-full bg-orange hover:bg-orangeHover text-white font-semibold py-3 rounded-lg transition-colors"
      >
        Invest now
      </button>
    </div>
  );
};

const RailRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between items-center text-14 py-2 border-b border-stone dark:border-slate-700 last:border-0">
    <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="text-vine dark:text-white">{value}</dd>
  </div>
);

const MobileActionBar: FC<{ offer: InvestOffer; onInvestNow: () => void }> = ({ offer, onInvestNow }) => (
  <div className="flex items-center gap-3">
    <div className="flex-1">
      <div className="text-xs text-gray-500 dark:text-gray-400">Offer</div>
      <div className="text-base font-semibold text-vine dark:text-white">{formatterGbp.format(offer.priceGbp)}</div>
    </div>
    <button
      type="button"
      onClick={onInvestNow}
      className="bg-orange hover:bg-orangeHover text-white font-semibold py-3 px-5 rounded-lg"
    >
      Invest now
    </button>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Skeleton helpers
// ────────────────────────────────────────────────────────────────────────────

const SkeletonList: FC<{ count: number }> = ({ count }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => (
      <Skeleton key={i} height={16} className="w-full" />
    ))}
  </div>
);

const TileGridSkeleton: FC = () => (
  <div className="grid grid-cols-2 md:grid-cols-[auto_1fr] gap-5">
    <Skeleton height={170} className="w-full md:w-[200px] col-span-2 md:col-span-1" />
    <div className="grid grid-cols-3 gap-2">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} height={60} />
      ))}
    </div>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Inline Radar Chart for Market Metrics
// ────────────────────────────────────────────────────────────────────────────

interface RadarChartProps {
  vintage: number;
  critic: number;
  fairValue: number;
  liquidity: number;
  performance: number;
  size?: number;
}

const RadarChart: FC<RadarChartProps> = ({ vintage, critic, fairValue, liquidity, performance, size = 170 }) => {
  const { isDark } = useTheme();
  const center = size / 2;
  const maxRadius = (size / 2) * 0.48;

  const angles = [-90, -90 + 72, -90 + 144, -90 + 216, -90 + 288];
  const toRadians = (deg: number) => (deg * Math.PI) / 180;

  const getPoint = (angle: number, value: number) => {
    const radius = (value / 100) * maxRadius;
    const rad = toRadians(angle);
    return { x: center + radius * Math.cos(rad), y: center + radius * Math.sin(rad) };
  };

  const getLabelPoint = (angle: number) => {
    const baseRadius = maxRadius + 22;
    const rad = toRadians(angle);
    return { x: center + baseRadius * Math.cos(rad), y: center + baseRadius * Math.sin(rad) };
  };

  const values = [vintage, critic, fairValue, liquidity, performance];
  const labels = ['Vintage', 'Critic', 'Fair Value', 'Liquidity', 'Perf.'];
  const gridLevels = [25, 50, 75, 100];

  const colors = {
    grid: isDark ? '#475569' : '#e5e7eb',
    axis: isDark ? '#64748b' : '#d1d5db',
    polygon: isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(107, 114, 128, 0.3)',
    polygonStroke: isDark ? '#94a3b8' : '#4b5563',
    point: isDark ? '#e2e8f0' : '#374151',
    label: isDark ? '#94a3b8' : '#6b7280',
    value: isDark ? '#e2e8f0' : '#374151',
  };

  const gridPaths = gridLevels.map((level) => {
    const points = angles.map((angle) => getPoint(angle, level));
    return points.map((p) => `${p.x},${p.y}`).join(' ');
  });

  const axisLines = angles.map((angle) => {
    const end = getPoint(angle, 100);
    return { x1: center, y1: center, x2: end.x, y2: end.y };
  });

  const dataPath = values
    .map((value, i) => getPoint(angles[i], value))
    .map((p) => `${p.x},${p.y}`)
    .join(' ');

  const labelPositions = angles.map((angle, i) => {
    const pos = getLabelPoint(angle);
    let textAnchor: 'start' | 'middle' | 'end' = 'middle';
    if (Math.abs(angle + 90) < 15) textAnchor = 'middle';
    else if (angle > -75 && angle < 90) textAnchor = 'start';
    else textAnchor = 'end';
    return { ...pos, label: labels[i], value: values[i], textAnchor };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
      {gridPaths.map((path, i) => (
        <polygon key={`grid-${i}`} points={path} fill="none" stroke={colors.grid} strokeWidth="1" />
      ))}
      {axisLines.map((line, i) => (
        <line
          key={`axis-${i}`}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke={colors.axis}
          strokeWidth="1"
        />
      ))}
      <polygon points={dataPath} fill={colors.polygon} stroke={colors.polygonStroke} strokeWidth="1.5" />
      {values.map((value, i) => {
        const point = getPoint(angles[i], value);
        return <circle key={`point-${i}`} cx={point.x} cy={point.y} r="3" fill={colors.point} />;
      })}
      {labelPositions.map((pos, i) => (
        <g key={`label-${i}`}>
          <text
            x={pos.x}
            y={pos.y - 5}
            textAnchor={pos.textAnchor}
            dominantBaseline="middle"
            style={{ fontSize: '9px', fill: colors.label }}
          >
            {pos.label}
          </text>
          <text
            x={pos.x}
            y={pos.y + 7}
            textAnchor={pos.textAnchor}
            dominantBaseline="middle"
            style={{ fontSize: '10px', fontWeight: 600, fill: colors.value }}
          >
            {pos.value ? Math.round(pos.value) : '-'}
          </text>
        </g>
      ))}
    </svg>
  );
};

export default OfferDetailModal;
